/**
 * DevPanel — a hidden panel for simulating failure scenarios on the Book page.
 * Revealed by Ctrl+Shift+K (or ?dev=1 in the URL). British English project.
 */
import { useState } from 'react';
import type { DevToolsState } from '../lib/devTools';
import { saveDevTools } from '../lib/devTools';
import { api } from '../lib/api';
import './DevPanel.css';

interface DevPanelProps {
  state: DevToolsState;
  onChange: (state: DevToolsState) => void;
  onClose: () => void;
  /** The active booking ID (if any) — used by the test-reminder button. */
  bookingId?: string;
}

export function DevPanel({ state, onChange, onClose, bookingId }: DevPanelProps) {
  const [local, setLocal] = useState<DevToolsState>(state);
  // §9.4 — test-reminder UI state. The ADMIN_SYNC_TOKEN is a server-side secret
  // (not in the client bundle), so the tester pastes it here. Stored in
  // sessionStorage (not localStorage) so it doesn't persist across sessions.
  const [adminToken, setAdminToken] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return window.sessionStorage.getItem('itk-admin-token') ?? '';
  });
  const [reminderType, setReminderType] = useState<'24h' | '1h'>('24h');
  const [reminderDelay, setReminderDelay] = useState<number>(20); // default 20s
  const [reminderStatus, setReminderStatus] = useState<string | null>(null);
  const [reminderSending, setReminderSending] = useState(false);
  // §9.4 — optional countdown for the test-reminder (gives the tester time to
  // background the tab so the service worker's onBackgroundMessage fires).
  const [countdown, setCountdown] = useState<number | null>(null);

  const update = (patch: Partial<DevToolsState>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange(next);
    saveDevTools(next);
  };

  return (
    <div className="itk-devpanel" role="dialog" aria-label="Dev tools">
      <div className="itk-devpanel-head">
        <strong>Dev tools</strong>
        <button type="button" className="itk-devpanel-close" onClick={onClose} aria-label="Close dev tools">×</button>
      </div>
      <p className="itk-devpanel-sub">
        Simulate failure scenarios. Affects the next booking attempt only. Clears on reload unless persisted.
      </p>
      <label className="itk-devpanel-row">
        <input
          type="checkbox"
          checked={local.failCoachMatch}
          onChange={(e) => update({ failCoachMatch: e.target.checked, failBooking: e.target.checked ? false : local.failBooking })}
        />
        Simulate coach-match failure (falls back to Thomas Kelly)
      </label>
      <label className="itk-devpanel-row">
        <input
          type="checkbox"
          checked={local.failBooking}
          onChange={(e) => update({ failBooking: e.target.checked, failCoachMatch: e.target.checked ? false : local.failCoachMatch })}
        />
        Simulate booking failure (shows apology + phone fallback)
      </label>
      <div className="itk-devpanel-row">
        <label htmlFor="itk-dev-report" className="itk-devpanel-label">Report email override (for the failure alert):</label>
        <input
          id="itk-dev-report"
          type="email"
          className="itk-input"
          placeholder="you@innovate-design.com"
          value={local.reportEmail}
          onChange={(e) => update({ reportEmail: e.target.value })}
        />
      </div>
      <p className="itk-devpanel-hint">Tip: the simulated failure applies to the next request that matches the flag. Uncheck to disable.</p>

      <hr className="itk-devpanel-divider" />

      <div className="itk-devpanel-section">
        <strong>§9.4 — Test a booking reminder</strong>
        <p className="itk-devpanel-sub">
          Immediately dispatch a reminder for the current booking (bypasses Cloud Tasks scheduling).
          Exercises the full push/email dispatch chain. Requires the ADMIN_SYNC_TOKEN.
        </p>
        <div className="itk-devpanel-row">
          <label htmlFor="itk-dev-admin-token" className="itk-devpanel-label">Admin sync token:</label>
          <input
            id="itk-dev-admin-token"
            type="password"
            className="itk-input"
            placeholder="Paste ADMIN_SYNC_TOKEN"
            value={adminToken}
            onChange={(e) => {
              setAdminToken(e.target.value);
              if (typeof window !== 'undefined') {
                window.sessionStorage.setItem('itk-admin-token', e.target.value);
              }
            }}
          />
        </div>
        <div className="itk-devpanel-row">
          <label className="itk-devpanel-label">Reminder type:</label>
          <label className="itk-devpanel-inline">
            <input type="radio" name="reminder-type" checked={reminderType === '24h'} onChange={() => setReminderType('24h')} />
            24h
          </label>
          <label className="itk-devpanel-inline">
            <input type="radio" name="reminder-type" checked={reminderType === '1h'} onChange={() => setReminderType('1h')} />
            1h
          </label>
        </div>
        <div className="itk-devpanel-row">
          <label className="itk-devpanel-label">Delay before sending:</label>
          <label className="itk-devpanel-inline">
            <input
              type="radio"
              name="reminder-delay"
              checked={reminderDelay === 0}
              onChange={() => setReminderDelay(0)}
            />
            Immediate
          </label>
          <label className="itk-devpanel-inline">
            <input
              type="radio"
              name="reminder-delay"
              checked={reminderDelay === 20}
              onChange={() => setReminderDelay(20)}
            />
            20s (switch tab)
          </label>
        </div>
        <button
          type="button"
          className="itk-btn itk-devpanel-btn"
          disabled={!bookingId || !adminToken || reminderSending || countdown !== null}
          onClick={async () => {
            if (!bookingId || !adminToken) return;
            setReminderStatus(null);
            // If a delay is set, count down so the tester can background the tab
            // (the SW's onBackgroundMessage only fires when the app is in the
            // background). The foreground onMessage handler (in firebase.ts)
            // covers the immediate case.
            if (reminderDelay > 0) {
              setCountdown(reminderDelay);
              for (let i = reminderDelay; i > 0; i--) {
                setCountdown(i);
                await new Promise((r) => setTimeout(r, 1000));
              }
              setCountdown(null);
            }
            setReminderSending(true);
            try {
              const res = await api.post<{ ok: boolean; channel: string; pushed: number; emailSent: boolean }>(
                '/api/v1/admin/send-test-reminder',
                { bookingId, reminderType },
                undefined,
                { 'X-Sync-Token': adminToken },
              );
              setReminderStatus(`Sent — channel: ${res.channel}, pushed: ${res.pushed}, email: ${res.emailSent}`);
            } catch (err) {
              setReminderStatus(`Failed: ${err instanceof Error ? err.message : 'unknown error'}`);
            } finally {
              setReminderSending(false);
            }
          }}
        >
          {countdown !== null
            ? `Sending in ${countdown}s… (switch tab now)`
            : reminderSending
              ? 'Sending…'
              : `Send test ${reminderType} reminder`}
        </button>
        {!bookingId && (
          <p className="itk-devpanel-hint">No active booking — book a call first, then reopen this panel.</p>
        )}
        {reminderStatus && (
          <p className="itk-devpanel-hint" role="status">{reminderStatus}</p>
        )}
      </div>
    </div>
  );
}